import React from 'react'
import styled, { css } from 'styled-components'
import LangIcon from '../icons/IconLib/GlobeIcon'
import { langList } from '../artcoded/langs'
import { useCookies } from 'react-cookie';
import { useDetectOutsideClick } from '../hooks/useDetectOutsideClick'
import i18next from 'i18next'

type Variant = 'icon' | 'initials'
type DropDirection = 'down' | 'up'

interface Props {
  /** 'icon' (default) shows the globe; 'initials' shows the active language code (e.g. EN). */
  variant?: Variant
  /** Direction the dropdown opens. 'down' (default) anchors below the trigger, 'up' above. */
  dropDirection?: DropDirection
  /** Chip-style sizing matches the rest of the SidebarFooter chips (2.25rem). Default keeps the original 3em. */
  chip?: boolean
  className?: string
}

const LangSwitcher: React.FC<Props> = ({
  variant = 'icon',
  dropDirection = 'down',
  chip = false,
  className,
}) => {
  const [cookies, setCookies] = useCookies(['i18next'])
  const [isOpenMenu, setIsOpenMenu] = React.useState(false)
  const ref = React.useRef<HTMLDivElement | null>(null)

  const handleCloseMenu = () => {
    setIsOpenMenu(false)
  }
  const handleOpenMenu = () => {
    setIsOpenMenu(true)
  }
  const handleSelectLang = (e: React.MouseEvent<HTMLElement>, code: string) => {
    e.stopPropagation()
    setCookies('i18next', code)
    i18next.changeLanguage(code)
    handleCloseMenu()
    // reload page
    window.location.reload()
  }
  useDetectOutsideClick(ref, handleCloseMenu)

  const activeCode = (cookies.i18next as string | undefined) ?? i18next.language ?? 'he'

  return (
    <MainWrapper
      ref={ref}
      onClick={handleOpenMenu}
      $chip={chip}
      className={className}
    >
      {variant === 'initials' ? (
        <Initials>{activeCode.slice(0, 2).toUpperCase()}</Initials>
      ) : (
        <LangIcon />
      )}
      <MenuWrapper $isOpen={isOpenMenu} $direction={dropDirection}>
        {
          langList.map((lang, index) =>
            <Item
              key={index}
              onClick={(e) => handleSelectLang(e, lang.code)}
              disabled={lang.code === cookies.i18next}
            >
              {lang.name}
            </Item>
          )
        }
      </MenuWrapper>
    </MainWrapper>
  )
}

export default LangSwitcher

const MainWrapper = styled.div<{ $chip: boolean }>`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.3s;
  cursor: pointer;

  ${({ $chip, theme }) =>
    $chip
      ? css`
          width: 2.25rem;
          height: 2.25rem;
          border-radius: 0.5rem;
          background: transparent;
          color: ${theme.colors.icon};
          &:hover {
            color: ${theme.colors.ink};
          }
        `
      : css`
          width: 3em;
          height: 3em;
          background: #fff;
          border-radius: 50%;
          svg {
            width: 1.5em; height: 1.5em;
            color: #297f76;
          }
          &:hover svg {
            transform: scale(1.1);
          }
        `}
`
const MenuWrapper = styled.div<{ $isOpen: boolean; $direction: DropDirection }>`
  position: absolute;
  background: #fff;
  left: 0;
  ${({ $direction }) =>
    $direction === 'up'
      ? css`
          bottom: calc(100% + 0.4em);
        `
      : css`
          top: calc(100% + 0.2em);
        `}
  padding: 0.5em;
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  z-index: 1;
  flex-direction: column;
  gap: 0.1em;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  border-radius: 10px;
`
const Item = styled.button`
  padding: 0.5em;
  display: flex;
  justify-content: flex-end;
  gap: 0.5em;
  outline: none;
  border: none;
  font-size: 1.05em;
  cursor: pointer;
  background: #fff;
  border-radius: 10px;
  color: #297F76;
  &:disabled {
    color: #ccc;
    cursor: not-allowed;
    &:hover {
      background: #fff;

    }
  }
  &:hover {
    background: #f5f5f5;
  }
`

const Initials = styled.span`
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: inherit;
  user-select: none;
`
